import apiClient from './client';
import { API_CONFIG } from '@/config/constants';
import { debug } from '@/utils/debug';

/**
 * Meta (Facebook/Instagram) Ads linkage API client.
 *
 * Mirrors `googleAdsApi` (./googleAds.ts) for the OAuth connect flow per
 * META_INTEGRATION_PLAN.md §4. Backend contract:
 * src/api/routers/meta_ads_linking.py.
 *
 * The `/auth/meta/authorize` endpoint is gated by
 * `require_new_meta_linkage_allowed` (free-tier single-platform lock —
 * see docs/FREE_TIER_SINGLE_PLATFORM_PLAN.md). Free-tier members who
 * already linked Google get a 409 with `{upgrade_required: true}`. UI
 * consumes `useLinkageEligibilityQuery` to hide the entry point ahead
 * of time; this client just calls the endpoints and surfaces errors.
 * Other Meta routes keep `require_meta_linkage_access` as a vestigial
 * kill switch — currently a no-op.
 */

export interface MetaAdAccountSummary {
  meta_ad_account_id: string;
  account_name: string | null;
  currency: string | null;
  timezone_name: string | null;
  business_id: string | null;
  business_name: string | null;
}

export interface MetaLinkedAccountSummary {
  meta_ad_account_id: string;
  account_name: string | null;
  business_name: string | null;
  /** Phase 0.5 — ISO 4217 currency code captured at OAuth link time.
   *  Read by `usePoolCurrency` so the cross-platform currency guard
   *  in `BudgetOptimizerSimple` can detect mixed-currency pools. Null
   *  for legacy rows linked before the column was populated. */
  account_currency?: string | null;
  // A2.3 §4.2 — token expiry surfaced here so MetaTokenExpiringBanner can
  // read it directly via authStore.linkedMetaAccountDetails (populated by
  // preloadLinkedAccounts on login). Backend reuses the 7-day
  // TOKEN_REPROMPT_WINDOW; token_expires_at is the ISO 8601 timestamp.
  token_expiring_soon?: boolean;
  token_expires_at?: string | null;
  /** A4.3.2 — default Facebook Page ID linked at OAuth time, if any.
   *  Read by `CreateAdSetWizard` for the LEADS path so it can skip the
   *  page-picker UI when a default exists. Null when the user never
   *  picked a page during link setup. */
  page_id?: string | null;
  /** Migration 013 — Meta display name from /me?fields=name
   *  (public_profile). Read by `MetaIdentityChip` so the user can
   *  verify which Meta identity they linked as. Null for pre-013 rows
   *  pending backfill. */
  meta_user_name?: string | null;
  /** Migration 013 — Public Graph picture URL composed server-side
   *  from `meta_user_id`. Null when meta_user_id hasn't been
   *  backfilled — frontend falls back to an initials avatar. */
  meta_user_picture_url?: string | null;
}

export interface MetaAuthorizeResponse {
  authorization_url: string;
  state: string;
}

export interface MetaLinkAccountResponse {
  meta_ad_account_id: string;
  is_linked: boolean;
  message: string;
}

export interface MetaDisconnectResponse {
  meta_ad_account_id: string;
  disconnected: boolean;
}

export interface MetaLinkStatusResponse {
  meta_ad_account_id: string;
  is_active: boolean;
  token_expiring_soon: boolean;
  token_expires_at: string | null;
}

export interface MetaCompleteSetupResponse {
  success: boolean;
  message: string;
  meta_ad_account_id: string;
  data_sync_triggered: boolean;
  // Machine-readable classification when success=false. Frontend maps to
  // distinct error UX: 'threshold_rejected' → "Account Not Eligible",
  // 'sync_failed' → "Setup Could Not Complete". Absent on success.
  // Unknown kinds fall back to threshold_rejected copy for safety.
  reason_kind?: 'threshold_rejected' | 'sync_failed' | string;
}

// ── Batch update wire types (A4.2 status/rename + Phase 1b budget) ──────────
//
// Mirror the backend Pydantic models in `src/api/models/meta_campaign.py`.
// Wire field names use snake_case (Pydantic default) — the pendingChangesStore
// translates camelCase to snake_case at the boundary.

export type MetaBatchEntityType = 'campaign' | 'ad_set' | 'ad';
export type MetaBatchStatusAction = 'pause' | 'enable' | 'archive';
export type MetaBatchOperation = 'status' | 'rename' | 'budget';

export interface MetaBatchStatusChangePayload {
  entity_type: MetaBatchEntityType;
  entity_id: string;
  action: MetaBatchStatusAction;
}

export interface MetaBatchRenameChangePayload {
  entity_type: MetaBatchEntityType;
  entity_id: string;
  name: string;
}

/**
 * Phase 1b — CBO campaign-level budget change variant. `entity_type` is
 * `'campaign'`-only because Meta's budget surface for Phase 1 is CBO
 * (campaign-level) only — ABO ad-set-level is deferred (Phase 2). Mirrors
 * the backend Pydantic `MetaBatchBudgetChange` Literal-of-one to defeat
 * silent acceptance of an `ad_set` entry the dispatcher couldn't
 * actually apply. `daily_budget` is in MAJOR units (dollars/yen/etc.);
 * the backend converts to Meta wire-shape minor units at the API
 * boundary via `from_optimizer_unit`.
 */
export interface MetaBatchBudgetChangePayload {
  entity_type: 'campaign';
  entity_id: string;
  daily_budget: number;
}

export interface MetaBatchUpdateResult {
  entity_type: MetaBatchEntityType;
  entity_id: string;
  operation: MetaBatchOperation;
  // Status / rename success value. Null for budget operations — those
  // use `new_budget` instead (typed sibling, not string-coerced).
  new_value?: string | null;
  // Budget success value (major units). Sibling to `new_value` so
  // consumers can type-narrow on `operation`. Mirrors the backend
  // `MetaBatchUpdateResult.new_budget` field.
  new_budget?: number | null;
  error?: string | null;
  /** Server-authoritative snapshot of the applied entity. Not populated
   *  for batch ops today (each successful entity would require a follow-
   *  up app_configuration read); reserved for a future backend enrichment
   *  where the batch endpoint tracks per-entity applied state without an
   *  extra DB round trip. Shape mirrors Meta{Campaign,AdSet,Ad}. */
  applied_entity?: Record<string, unknown> | null;
}

export interface MetaBatchUpdateResponse {
  successful_updates: MetaBatchUpdateResult[];
  failed_updates: MetaBatchUpdateResult[];
}

// A4.3.1 — CreateMetaCampaignWizard. Payload mirrors the backend Pydantic
// model exactly (snake_case) to defeat Theme #3 (FE/BE param drift). The
// create-campaign request model uses `ConfigDict(extra="forbid")` so an
// unknown / mis-cased key returns 422 — surfacing the drift loudly instead
// of silently no-op'ing the field. (Many other endpoints in this codebase
// default to `extra="ignore"`, so the rule isn't repo-wide; check the model.)
export type MetaCampaignObjective =
  | 'OUTCOME_SALES'
  | 'OUTCOME_TRAFFIC'
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_APP_PROMOTION';

export type MetaSpecialAdCategory =
  | 'NONE'
  | 'HOUSING'
  | 'EMPLOYMENT'
  | 'CREDIT'
  | 'ISSUES_ELECTIONS_POLITICS';

/** Status at create time. PAUSED is the safe default; ACTIVE is exposed
 *  for parity with Google's wizard's `CampaignBasicsStep`. Meta won't
 *  actually serve or bill until ad sets + ads + creatives exist underneath
 *  the campaign, so ACTIVE-without-children is harmless. */
export type MetaCampaignCreateStatus = 'PAUSED' | 'ACTIVE';

/** Campaign-level bid strategies (CBO mode). Cap / floor values for the
 *  three constrained strategies are configured at the ad-set level — A4.3.2.
 *  Default matches Meta's own default for CBO campaigns. */
export type MetaBidStrategy =
  | 'LOWEST_COST_WITHOUT_CAP'
  | 'LOWEST_COST_WITH_BID_CAP'
  | 'COST_CAP'
  | 'LOWEST_COST_WITH_MIN_ROAS';

export interface CreateMetaCampaignRequest {
  meta_ad_account_id: string;
  campaign_name: string;
  objective: MetaCampaignObjective;
  /** Initial status. Defaults to PAUSED on the backend if omitted. */
  status?: MetaCampaignCreateStatus;
  /** Campaign-level bid strategy. Defaults to LOWEST_COST_WITHOUT_CAP on the backend. */
  bid_strategy?: MetaBidStrategy;
  /** USD dollars (major units). Backend converts to cents at the API boundary. */
  daily_budget?: number;
  /** USD dollars. Mutually exclusive with daily_budget. Requires stop_time. */
  lifetime_budget?: number;
  /** Must include exactly ['NONE'] OR one+ regulated categories. */
  special_ad_categories: MetaSpecialAdCategory[];
  /** YYYY-MM-DD or ISO 8601. Required (D7 strictness), cannot be in the past. */
  start_time: string;
  /** YYYY-MM-DD or ISO 8601. Required when lifetime_budget is set, optional otherwise. */
  stop_time?: string;
}

export interface CreateMetaCampaignResponse {
  campaign_id: string;
  campaign_name: string;
  objective: MetaCampaignObjective;
  /** Initial Meta-side status — echoes the `status` field on the request. */
  effective_status: MetaCampaignCreateStatus;
  success: boolean;
  message: string;
  /** Server-authoritative snapshot of the campaign as it was written to
   *  `app_configuration` (raw dict — includes `platform_source` and
   *  `ad_sets` that the MetaCampaign read model doesn't project). Optional
   *  so pre-enrichment servers don't 422 the frontend. When present,
   *  frontend splices this into the React Query cache — see
   *  docs/FRONTEND_MUTATION_PATTERNS.md. Cast at splice site to
   *  AppCampaignConfig. */
  applied_entity?: Record<string, unknown>;
  /** Non-fatal reconciliation warning surfaced when the Meta write
   *  succeeded but the local cached view couldn't be updated. */
  warning?: string | null;
}

// A4.3.2 — CreateAdSetWizard. Same snake_case posture as create-campaign
// (`extra="forbid"` on the backend model returns 422 on drift).

/** Optimization goals — subset of Meta's taxonomy that the wizard exposes.
 *  See backend `MetaOptimizationGoal` in `src/api/models/meta_ad_set.py`. */
export type MetaOptimizationGoal =
  | 'REACH'
  | 'IMPRESSIONS'
  | 'AD_RECALL_LIFT'
  | 'LINK_CLICKS'
  | 'LANDING_PAGE_VIEWS'
  | 'POST_ENGAGEMENT'
  | 'PAGE_LIKES'
  | 'EVENT_RESPONSES'
  | 'THRUPLAY'
  | 'VIDEO_VIEWS'
  | 'OFFSITE_CONVERSIONS'
  | 'VALUE'
  | 'LEAD_GENERATION'
  | 'QUALITY_LEAD'
  | 'APP_INSTALLS'
  | 'CONVERSATIONS';

/** Billing event — what Meta charges per. IMPRESSIONS is the universal
 *  default; named events pair only with their matching goal. */
export type MetaBillingEvent =
  | 'IMPRESSIONS'
  | 'LINK_CLICKS'
  | 'THRUPLAY'
  | 'POST_ENGAGEMENT'
  | 'PAGE_LIKES'
  | 'APP_INSTALLS';

export type MetaDestinationType =
  | 'WEBSITE'
  | 'APP'
  | 'MESSENGER'
  | 'INSTAGRAM_DIRECT'
  | 'ON_AD'
  | 'ON_POST'
  | 'ON_PAGE'
  | 'FACEBOOK'
  | 'WHATSAPP'
  | 'PHONE_CALL';

export type MetaCustomEventType =
  | 'PURCHASE'
  | 'LEAD'
  | 'COMPLETE_REGISTRATION'
  | 'ADD_TO_CART'
  | 'ADD_TO_WISHLIST'
  | 'ADD_PAYMENT_INFO'
  | 'CONTENT_VIEW'
  | 'INITIATE_CHECKOUT'
  | 'SEARCH'
  | 'SUBSCRIBE'
  | 'START_TRIAL'
  | 'SUBMIT_APPLICATION'
  | 'OTHER';

export type MetaPublisherPlatform =
  | 'facebook'
  | 'instagram'
  | 'audience_network'
  | 'messenger';

export type MetaFacebookPosition =
  | 'feed'
  | 'right_hand_column'
  | 'marketplace'
  | 'video_feeds'
  | 'story'
  | 'search'
  | 'instream_video'
  | 'facebook_reels'
  | 'facebook_reels_overlay'
  | 'biz_disco_feed'
  | 'profile_feed';

export type MetaInstagramPosition =
  | 'stream'
  | 'story'
  | 'explore'
  | 'reels'
  | 'ig_search'
  | 'profile_feed'
  | 'explore_home';

export type MetaDevicePlatform = 'mobile' | 'desktop';

export type MetaGender = 'MALE' | 'FEMALE';

export type MetaAdSetCreateStatus = 'PAUSED' | 'ACTIVE';

export interface MetaInterest {
  id: string;
  name: string;
}

export interface MetaGeoLocations {
  /** ISO-3166-1 alpha-2 country codes (e.g. ['US', 'GB']). At minimum one. */
  countries: string[];
}

export interface MetaTargetingSpec {
  geo_locations: MetaGeoLocations;
  age_min: number;
  age_max: number;
  genders: MetaGender[];
  interests: MetaInterest[];
  /** Empty = automatic placements (Meta picks). */
  publisher_platforms: MetaPublisherPlatform[];
  facebook_positions: MetaFacebookPosition[];
  instagram_positions: MetaInstagramPosition[];
  device_platforms: MetaDevicePlatform[];
  /** Meta locale IDs (numeric). Empty = no language restriction. */
  locales: number[];
  /** Advantage Audience — when true, Meta AI-broadens beyond the
   *  manual targeting set. Required field; the wizard sets it
   *  explicitly. A4.3.2.5. */
  advantage_audience: boolean;
  /** Advantage+ Placements — when true, Meta picks placements and the
   *  manual placement fields must be empty (backend validator
   *  enforces). Required field; the wizard sets it explicitly. The
   *  wizard's default is true to match A4.3.2's wire shape (which
   *  always sent empty placement keys — Meta's signal for Advantage+).
   *  A4.3.2.5. */
  advantage_placements: boolean;
}

export interface MetaPromotedObject {
  // SALES path
  pixel_id?: string;
  custom_event_type?: MetaCustomEventType;
  // LEADS path — only page_id; the form_id lives on the ad creative
  // (call_to_action.value.lead_gen_form_id), not the ad set.
  page_id?: string;
  // APP_PROMOTION path
  application_id?: string;
  object_store_url?: string;
}

export interface CreateMetaAdSetRequest {
  meta_ad_account_id: string;
  ad_set_name: string;
  optimization_goal: MetaOptimizationGoal;
  billing_event: MetaBillingEvent;
  /** Defaults to PAUSED on the backend if omitted. */
  status?: MetaAdSetCreateStatus;
  /** USD dollars. Backend converts to cents. */
  daily_budget?: number;
  /** USD dollars. Mutually exclusive with daily_budget. Requires end_time. */
  lifetime_budget?: number;
  /** Strategy-dependent input. For BID_CAP / COST_CAP this is the money
   *  cap in USD dollars (backend converts to cents). For
   *  LOWEST_COST_WITH_MIN_ROAS this is the ROAS goal as a decimal
   *  multiplier (e.g. 1.5 for a 1.5x return; backend converts to Meta's
   *  ×10000 wire unit). Required when the parent campaign's
   *  bid_strategy is constrained; backend rejects it otherwise. */
  bid_amount?: number;
  /** YYYY-MM-DD or ISO 8601. Required, cannot be in the past. */
  start_time: string;
  /** YYYY-MM-DD or ISO 8601. Required when lifetime_budget is set. */
  end_time?: string;
  targeting: MetaTargetingSpec;
  /** Required for SALES / LEADS / APP_PROMOTION parent objectives;
   *  rejected for others. Backend enforces against parent. */
  promoted_object?: MetaPromotedObject;
  destination_type?: MetaDestinationType;
}

export interface CreateMetaAdSetResponse {
  ad_set_id: string;
  ad_set_name: string;
  campaign_id: string;
  optimization_goal: MetaOptimizationGoal;
  billing_event: MetaBillingEvent;
  effective_status: MetaAdSetCreateStatus;
  success: boolean;
  message: string;
  /** See CreateMetaCampaignResponse.applied_entity. Cast at splice site
   *  to MetaAdSet. */
  applied_entity?: Record<string, unknown>;
  warning?: string | null;
}

// ─── A4.3.3 — Create Meta Ad ───────────────────────────────────────────────
//
// Mirrors `src/api/models/meta_ad.py`. Keep types here in lockstep with
// the Pydantic models on the backend per §16 — backend is the security
// boundary, frontend is a typed mirror.

/** Meta call-to-action enum. First entry (`LEARN_MORE`) is the wizard
 *  default per the matrix-default lesson. */
export type MetaCallToActionType =
  | 'LEARN_MORE'
  | 'SHOP_NOW'
  | 'SIGN_UP'
  | 'DOWNLOAD'
  | 'GET_QUOTE'
  | 'CONTACT_US'
  | 'BOOK_TRAVEL'
  | 'APPLY_NOW'
  | 'GET_OFFER'
  | 'SUBSCRIBE'
  | 'WATCH_MORE'
  | 'ORDER_NOW'
  | 'NO_BUTTON';

/** Initial status for new ads — same shape as campaign / ad-set create. */
export type MetaAdCreateStatus = 'PAUSED' | 'ACTIVE';

/** Single-image link ad. A4.3.3 ships this variant only; video / carousel
 *  slots land in A4.3.3.1. Discriminator `creative_type: 'link'` is on the
 *  type from day 1 so the wire shape stays stable when the union widens. */
export interface MetaLinkCreativeSpec {
  creative_type: 'link';
  /** Publicly reachable image URL — must be http(s). */
  image_url: string;
  /** Bold short text under the image. 40 char soft cap. */
  headline: string;
  /** Long-form message above the image. 125 char soft cap. */
  primary_text: string;
  /** Optional small text under the headline. 30 char soft cap. */
  description?: string;
  /** CTA button label. Defaults to LEARN_MORE on the backend. */
  call_to_action?: MetaCallToActionType;
  /** Destination URL the ad clicks through to — must be http(s). */
  link_url: string;
  /** Meta lead form ID. Required by the backend when the parent campaign
   *  objective is OUTCOME_LEADS; ignored otherwise. */
  lead_gen_form_id?: string;
}

/** User picked an existing creative from the CreativeLibraryPicker. */
export interface MetaCreativeSourceExisting {
  mode: 'existing';
  /** Bare numeric Meta creative ID. */
  creative_id: string;
}

/** User built a creative in the wizard. A4.3.3 ships the Link variant
 *  only; A4.3.3.1 widens `spec` to a discriminated union. */
export interface MetaCreativeSourceNew {
  mode: 'new';
  spec: MetaLinkCreativeSpec;
}

export type MetaCreativeSource =
  | MetaCreativeSourceExisting
  | MetaCreativeSourceNew;

/** Advantage+ Creative variants toggle (§9.0.2). Defaults to off — Standard
 *  creative, no auto-variation. Backend's `_build_degrees_of_freedom_spec`
 *  centralizes the wire shape per §13. */
export interface MetaAdvantagePlusCreativeSpec {
  enabled: boolean;
  /** Only consulted when `enabled=true`. */
  text_variations: boolean;
  /** Only consulted when `enabled=true`. */
  image_variations: boolean;
}

export interface CreateMetaAdRequest {
  meta_ad_account_id: string;
  /** 1-400 chars, no control chars. */
  ad_name: string;
  /** Defaults to PAUSED on the backend if omitted. */
  status?: MetaAdCreateStatus;
  /** Facebook Page ID owning the ad. Required. */
  page_id: string;
  /** Optional Instagram actor for IG-served placements. */
  instagram_actor_id?: string;
  creative: MetaCreativeSource;
  /** Defaults to disabled on the backend if omitted. */
  advantage_plus_creative?: MetaAdvantagePlusCreativeSpec;
}

export interface CreateMetaAdResponse {
  ad_id: string;
  ad_name: string;
  ad_set_id: string;
  creative_id: string;
  effective_status: MetaAdCreateStatus;
  success: boolean;
  message: string;
  /** See CreateMetaCampaignResponse.applied_entity. Cast at splice site
   *  to MetaAd. */
  applied_entity?: Record<string, unknown>;
  warning?: string | null;
}

/** Derived discriminator for the picker UI's filter chips. Backend
 *  derives this from `object_story_spec` shape — `video_data` → "video",
 *  `link_data.child_attachments` → "carousel", `link_data` only →
 *  "image". */
export type MetaAdCreativeKind = 'image' | 'video' | 'carousel' | 'unknown';

/** One creative in the CreativeLibraryPicker's grid. Projected from
 *  `meta_ads_data.ad_creatives_daily` — one row per creative_id (latest
 *  snapshot). */
export interface MetaAdCreative {
  creative_id: string;
  creative_name?: string | null;
  image_url?: string | null;
  video_id?: string | null;
  thumbnail_url?: string | null;
  call_to_action_type?: string | null;
  link_url?: string | null;
  kind: MetaAdCreativeKind;
  /** Raw object_story_spec pass-through. The picker can extract
   *  headline / description / CTA without a second round-trip. */
  object_story_spec?: Record<string, unknown> | null;
}

// ── Ad editor (A4.3.x — post-creation edit surface) ─────────────────────────
//
// Mirrors the RSAEditor / RDAEditor UX shape on the Google side. Reads live
// from Meta (not the daily snapshot) so the editor reflects Ads-Manager-side
// edits since the last ingest. On save, if the creative changed, the backend
// archive-and-recreates (Meta creatives aren't PATCH-able) and returns both
// `old_creative_id` and `new_creative_id` so the frontend can splice cache.

/** Response from `GET /meta/ad-accounts/{acct}/ads/{id}`. */
export interface GetMetaAdResponse {
  ad_id: string;
  ad_name: string;
  /** Author-facing status (PAUSED / ACTIVE / ARCHIVED / DELETED). */
  status: string;
  /** Meta's computed status — includes review verdicts (PENDING_REVIEW,
   *  DISAPPROVED). Read-only for the editor; surfaces as a warning banner
   *  when non-authorable. */
  effective_status: string;
  ad_set_id: string;
  campaign_id: string;
  /** Parent campaign's objective. When `OUTCOME_LEADS`, the editor
   *  renders the lead-form picker. Absent for video / carousel ads
   *  (lookup skipped since copy editing isn't supported anyway). */
  campaign_objective?: string | null;
  page_id?: string | null;
  instagram_actor_id?: string | null;
  creative_id: string;
  creative_name?: string | null;
  /** Derived server-side via the shared `derive_creative_kind` helper —
   *  same rules the picker uses. Values: image | video | carousel |
   *  unknown. Editor renders the copy fields only for "image". */
  creative_kind: MetaAdCreativeKind;
  /** Populated only when `creative_kind === 'image'` AND the creative's
   *  fields all validate against `LinkCreativeSpec`. When null, the
   *  editor renders a read-only preview from `raw_object_story_spec`
   *  and gates copy edits behind an "edit in Ads Manager" notice. */
  creative_spec?: MetaLinkCreativeSpec | null;
  /** Raw pass-through for the read-only preview branch. */
  raw_object_story_spec?: Record<string, unknown> | null;
  thumbnail_url?: string | null;
}

/** Body for `PATCH /meta/ad-accounts/{acct}/ads/{id}`.
 *
 *  All fields optional; only submitted fields are applied. When
 *  `creative` is present the backend triggers archive-and-recreate. */
export interface UpdateMetaAdRequest {
  ad_name?: string;
  status?: 'PAUSED' | 'ACTIVE' | 'ARCHIVED';
  /** Reuses the wizard's `MetaCreativeSourceNew` shape — link-only
   *  today. When the wizard widens `spec` to include Video/Carousel,
   *  this endpoint inherits the new variants without a schema change. */
  creative?: MetaCreativeSourceNew;
  /** Carried through on recreate — Meta requires
   *  `degrees_of_freedom_spec` on the /adcreatives POST, not /ads. */
  advantage_plus_creative?: MetaAdvantagePlusCreativeSpec;
}

export interface UpdateMetaAdResponse {
  success: boolean;
  ad_id: string;
  /** Post-update ad name. Populated only when the request changed
   *  `ad_name`; undefined otherwise. Frontend cache splices are
   *  gated on the dirty-field check, so undefined means "leave the
   *  local value alone." */
  ad_name?: string | null;
  /** Post-update status. Same echo semantics as ad_name. */
  status?: string | null;
  /** Populated only when the request triggered a creative recreate. */
  old_creative_id?: string | null;
  /** Populated only when the request triggered a creative recreate. */
  new_creative_id?: string | null;
  message: string;
}

export interface MetaPixel {
  id: string;
  name: string;
}

export interface MetaLeadForm {
  id: string;
  name: string;
  status?: string | null;
}

export interface MetaPage {
  id: string;
  name: string;
}

export const metaAdsApi = {
  /**
   * Start the OAuth flow. Returns `{authorization_url, state}`. The frontend
   * should stash `state` in sessionStorage and then redirect the window to
   * `authorization_url`. The backend has already persisted the state in
   * `oauth_state` with `provider='meta'`.
   */
  async getAuthorizationUrl(): Promise<MetaAuthorizeResponse> {
    debug.log('metaAdsApi: Requesting Meta authorization URL');
    const response = await apiClient.get<MetaAuthorizeResponse>(
      '/auth/meta/authorize',
    );
    debug.log('metaAdsApi: Got authorization URL');
    return response.data;
  },

  /**
   * After the OAuth round-trip lands at /oauth/callback/meta, call this to
   * fetch the user's Meta ad accounts. The backend reads the pending token
   * off the `oauth_state` row using the supplied state header; the state is
   * **not** consumed here (only by linkAccount), so this can be retried if
   * the user refreshes the picker.
   */
  async getAccountsForState(state: string): Promise<MetaAdAccountSummary[]> {
    debug.log('metaAdsApi: Fetching ad accounts for OAuth state');
    const response = await apiClient.get<{ accounts: MetaAdAccountSummary[] }>(
      '/meta/accounts',
      {
        headers: { 'X-Meta-OAuth-State': state },
      },
    );
    debug.log('metaAdsApi: Got accounts list', {
      count: response.data.accounts?.length ?? 0,
    });
    return response.data.accounts ?? [];
  },

  /**
   * Link the chosen ad account. Consumes the state token server-side
   * (is_used=true, pending_access_token cleared) and upserts the credential
   * row. Subsequent calls with the same state will 400.
   */
  async linkAccount(input: {
    meta_ad_account_id: string;
    state: string;
    // Optional metadata captured from the picker so the backend can persist
    // it without an extra /me/adaccounts round-trip. See backend comment in
    // `MetaLinkAccountRequest` (src/api/routers/meta_ads_linking.py).
    account_name?: string | null;
    currency?: string | null;
    timezone_name?: string | null;
    business_id?: string | null;
    business_name?: string | null;
  }): Promise<MetaLinkAccountResponse> {
    debug.log('metaAdsApi: Linking Meta account', {
      meta_ad_account_id: input.meta_ad_account_id,
    });
    const response = await apiClient.post<MetaLinkAccountResponse>(
      '/meta/link-account',
      input,
    );
    debug.log('metaAdsApi: Link response', response.data);
    return response.data;
  },

  /**
   * Lightweight list of already-linked Meta accounts (DB-only read). Used by
   * authStore.preloadLinkedAccounts on app boot to populate
   * `linkedMetaAccountDetails`.
   *
   * NOTE: non-allowlisted users will 403 here every login. The caller (auth
   * store) swallows 403 specifically.
   */
  async getLinkedAccountsSummary(): Promise<MetaLinkedAccountSummary[]> {
    debug.log('metaAdsApi: Fetching linked Meta accounts summary');
    const response = await apiClient.get<{
      accounts: MetaLinkedAccountSummary[];
    }>('/meta/accounts-summary');
    debug.log('metaAdsApi: Linked accounts summary', {
      count: response.data.accounts?.length ?? 0,
    });
    return response.data.accounts ?? [];
  },

  /**
   * Trigger the 90-day Meta backfill via the Cloud Run ingest pipeline
   * (filtered to the current member). Blocks up to 180s — the backend
   * itself blocks up to 300s while Cloud Run runs the per-member ingest,
   * but 180s matches Google's tested budget (see googleAds.ts:76-88).
   *
   * If the request times out client-side, the Cloud Run job may still
   * complete in the background; the caller can treat timeout as "linked,
   * data still syncing" rather than an error.
   */
  async completeSetup(
    metaAdAccountId: string,
  ): Promise<MetaCompleteSetupResponse> {
    debug.log('metaAdsApi: Completing Meta account setup', { metaAdAccountId });
    const response = await apiClient.post<MetaCompleteSetupResponse>(
      `/meta/complete-setup/${encodeURIComponent(metaAdAccountId)}`,
      {},
      { timeout: API_CONFIG.COMPLETE_SETUP_TIMEOUT_MS },
    );
    debug.log('metaAdsApi: Setup completion response', response.data);
    return response.data;
  },

  /**
   * Soft-delete: flips `meta_ads_credentials.is_active=false`. Exposed for
   * parity with the Google client. No A1 UI calls this yet.
   */
  async disconnectAccount(
    meta_ad_account_id: string,
  ): Promise<MetaDisconnectResponse> {
    debug.log('metaAdsApi: Disconnecting Meta account', {
      meta_ad_account_id,
    });
    const response = await apiClient.delete<MetaDisconnectResponse>(
      `/meta/disconnect/${encodeURIComponent(meta_ad_account_id)}`,
    );
    debug.log('metaAdsApi: Disconnect response', response.data);
    return response.data;
  },

  /**
   * Fan a batch of Meta status + rename changes through `/meta/batch-update`
   * in a single round trip. Per-entity failures land in `failed_updates`;
   * `successful_updates` carries the applied entity rows. The HTTP response
   * is 200 even when some entities failed — callers branch on the per-row
   * result for granular toasts.
   *
   * Mirrors `googleAdsApi.batchUpdateKeywordBids`'s shape (successful /
   * failed split) so the SaveChangesBar consumer is uniform across
   * platforms. `memberUuid` is unused by the backend (the JWT carries
   * member_id) but kept as a positional arg for parity with the Google
   * client signature.
   */
  async batchUpdate(
    _memberUuid: string,
    statusChanges: MetaBatchStatusChangePayload[],
    renameChanges: MetaBatchRenameChangePayload[],
    budgetChanges: MetaBatchBudgetChangePayload[] = [],
  ): Promise<MetaBatchUpdateResponse> {
    debug.log('metaAdsApi: Dispatching batch update', {
      status: statusChanges.length,
      rename: renameChanges.length,
      budget: budgetChanges.length,
    });
    const response = await apiClient.post<MetaBatchUpdateResponse>(
      '/meta/batch-update',
      {
        status_changes: statusChanges,
        rename_changes: renameChanges,
        budget_changes: budgetChanges,
      },
    );
    debug.log('metaAdsApi: Batch update response', {
      successful: response.data.successful_updates.length,
      failed: response.data.failed_updates.length,
    });
    return response.data;
  },

  /**
   * Create a new Meta campaign (A4.3.1). Initial `status` is the caller's
   * choice — PAUSED (the wizard default) is the safer pick since a campaign
   * with no ad sets / ads / creatives has nothing to serve anyway; ACTIVE
   * is accepted by the backend for parity with Google's wizard. The user
   * flips between PAUSED and ACTIVE later via the A4.2 status-toggle route.
   *
   * Payload uses snake_case (`campaign_name`, `daily_budget`, etc.) to match
   * the backend Pydantic `CreateMetaCampaignRequest` exactly. The model is
   * configured `extra="forbid"` so a casing mismatch produces a 422, not a
   * silent no-op. Theme #3 (FE/BE param drift) — the loud-fail posture is
   * specifically chosen for this surface; verify the model before assuming
   * other routes behave the same way.
   */
  async createCampaign(
    request: CreateMetaCampaignRequest,
  ): Promise<CreateMetaCampaignResponse> {
    debug.log('metaAdsApi: Creating Meta campaign', {
      account: request.meta_ad_account_id,
      objective: request.objective,
      budgetMode: request.daily_budget != null ? 'daily' : 'lifetime',
    });
    const response = await apiClient.post<CreateMetaCampaignResponse>(
      '/meta/campaigns',
      request,
    );
    debug.log('metaAdsApi: Create campaign response', {
      campaign_id: response.data.campaign_id,
      effective_status: response.data.effective_status,
    });
    return response.data;
  },

  /**
   * Create a new Meta ad set under an existing campaign (A4.3.2). Same
   * snake_case posture as createCampaign — backend uses `extra="forbid"` so
   * casing drift returns 422 not silent no-op (Theme #3).
   *
   * Parent campaign context (objective / bid_strategy /
   * special_ad_categories) is re-loaded backend-side from app_config (with
   * Meta-API fallback) — the frontend `parentCampaign` prop is for
   * UX-side option filtering only, not the source of truth.
   */
  async createAdSet(
    campaignId: string,
    request: CreateMetaAdSetRequest,
  ): Promise<CreateMetaAdSetResponse> {
    // budgetMode is a tri-state: 'daily' when daily_budget is set,
    // 'lifetime' when lifetime_budget is set, 'cbo-inherited' when
    // neither is set (parent campaign carries the budget under
    // Campaign Budget Optimization).
    const budgetMode =
      request.daily_budget != null
        ? 'daily'
        : request.lifetime_budget != null
        ? 'lifetime'
        : 'cbo-inherited';
    debug.log('metaAdsApi: Creating Meta ad set', {
      campaign_id: campaignId,
      account: request.meta_ad_account_id,
      optimization_goal: request.optimization_goal,
      billing_event: request.billing_event,
      budgetMode,
    });
    const response = await apiClient.post<CreateMetaAdSetResponse>(
      `/meta/campaigns/${encodeURIComponent(campaignId)}/ad-sets`,
      request,
    );
    debug.log('metaAdsApi: Create ad set response', {
      ad_set_id: response.data.ad_set_id,
      effective_status: response.data.effective_status,
    });
    return response.data;
  },

  /**
   * Create a Meta ad under an existing ad set (A4.3.3).
   *
   * Branches on `request.creative.mode`: 'existing' uses a creative_id
   * picked from CreativeLibraryPicker; 'new' builds the creative inline
   * (Link variant in this PR).
   *
   * Advantage+ Creative (`request.advantage_plus_creative.enabled`) sends
   * `degrees_of_freedom_spec` on the wire — see §9.0.2.
   */
  async createAd(
    adSetId: string,
    request: CreateMetaAdRequest,
  ): Promise<CreateMetaAdResponse> {
    debug.log('metaAdsApi: Creating Meta ad', {
      ad_set_id: adSetId,
      account: request.meta_ad_account_id,
      creative_mode: request.creative.mode,
      advantage_plus_enabled:
        request.advantage_plus_creative?.enabled ?? false,
    });
    const response = await apiClient.post<CreateMetaAdResponse>(
      `/meta/ad-sets/${encodeURIComponent(adSetId)}/ads`,
      request,
    );
    debug.log('metaAdsApi: Create ad response', {
      ad_id: response.data.ad_id,
      creative_id: response.data.creative_id,
      effective_status: response.data.effective_status,
    });
    return response.data;
  },

  /**
   * List existing ad creatives for the CreativeLibraryPicker (A4.3.3).
   *
   * Returns one page (latest snapshot per creative_id) from
   * `meta_ads_data.ad_creatives_daily`, scoped to the authenticated
   * member + ad-account. The picker filters / searches client-side
   * within the loaded pages.
   *
   * Cursor pagination: pass `after = response.next_cursor` to fetch
   * the next page. `next_cursor === null` means the current page is
   * the last. Backend default page size is 100; max 500.
   */
  async listAdCreatives(
    metaAdAccountId: string,
    opts: { limit?: number; after?: string | null } = {},
  ): Promise<{ creatives: MetaAdCreative[]; next_cursor: string | null }> {
    const params: Record<string, string | number> = {
      meta_ad_account_id: metaAdAccountId,
    };
    if (opts.limit != null) params.limit = opts.limit;
    // `!= null` rather than truthy: Meta creative IDs are big-int
    // strings and a cursor of '0' is a theoretically valid (if unusual)
    // value. Truthy-coercion would silently drop it, breaking the
    // last-page detection for that edge case.
    if (opts.after != null) params.after = opts.after;
    const response = await apiClient.get<{
      creatives: MetaAdCreative[];
      next_cursor: string | null;
    }>('/meta/ad-creatives', { params });
    return {
      creatives: response.data.creatives ?? [],
      next_cursor: response.data.next_cursor ?? null,
    };
  },

  /**
   * Interest / behavior / demographic autocomplete proxy
   * (`GET /meta/targeting-search`). Used by the ad-set wizard's targeting
   * step. Backend caps results at 50; UI typeahead surfaces ~20 visible.
   *
   * Debounce on the caller side (~300ms). The backend route layers a
   * 60s in-memory TTL cache keyed on (q.lower(), search_type, ad_account_id)
   * — repeat queries within the window skip the Meta round-trip.
   */
  async searchTargeting(
    adAccountId: string,
    query: string,
    type:
      | 'adinterest'
      | 'adworkemployer'
      | 'adworkposition'
      | 'education'
      | 'family_statuses'
      | 'adlocale' = 'adinterest',
  ): Promise<MetaInterest[]> {
    const response = await apiClient.get<{ interests: MetaInterest[] }>(
      '/meta/targeting-search',
      { params: { q: query, ad_account_id: adAccountId, search_type: type } },
    );
    return response.data.interests ?? [];
  },

  /**
   * Pixel picker for the SALES promoted_object step
   * (`GET /meta/ad-accounts/{id}/pixels`). React Query should cache with
   * `staleTime: 5 * 60 * 1000` (5 min) — pixels are stable per account.
   */
  async listPixels(adAccountId: string): Promise<MetaPixel[]> {
    const response = await apiClient.get<{ pixels: MetaPixel[] }>(
      `/meta/ad-accounts/${encodeURIComponent(adAccountId)}/pixels`,
    );
    return response.data.pixels ?? [];
  },

  /**
   * Page picker for the LEADS promoted_object step when the credentials
   * row's `page_id` is null (no default page picked at link time).
   * `adAccountId` is needed to load the user's Meta token; the
   * `/me/accounts` call itself is user-scoped (returns all pages the
   * user can promote).
   */
  async listPages(adAccountId: string): Promise<MetaPage[]> {
    const response = await apiClient.get<{ pages: MetaPage[] }>(
      '/meta/pages',
      { params: { ad_account_id: adAccountId } },
    );
    return response.data.pages ?? [];
  },

  /**
   * Lead-form picker for the LEADS promoted_object step
   * (`GET /meta/pages/{page_id}/lead-forms`). `adAccountId` is needed for
   * the token load; the lead-forms list itself is page-scoped.
   */
  async listLeadForms(
    pageId: string,
    adAccountId: string,
  ): Promise<MetaLeadForm[]> {
    const response = await apiClient.get<{ lead_forms: MetaLeadForm[] }>(
      `/meta/pages/${encodeURIComponent(pageId)}/lead-forms`,
      { params: { ad_account_id: adAccountId } },
    );
    return response.data.lead_forms ?? [];
  },

  /**
   * Ad editor read — fetch a single ad + its linked creative live from
   * Meta so the editor sees current state (not the daily snapshot).
   * Backend does one round-trip via `creative{...}` field expansion.
   *
   * Errors: 400 (ad not found / bad account), 409 (no Meta credential —
   * frontend re-auth via MetaTokenExpiringBanner), 502 (Meta 5xx),
   * 504 (Meta timeout).
   */
  async getAd(
    adAccountId: string,
    adId: string,
  ): Promise<GetMetaAdResponse> {
    debug.log('metaAdsApi: Fetching Meta ad for editor', {
      ad_account_id: adAccountId,
      ad_id: adId,
    });
    const response = await apiClient.get<GetMetaAdResponse>(
      `/meta/ad-accounts/${encodeURIComponent(adAccountId)}/ads/${encodeURIComponent(adId)}`,
    );
    debug.log('metaAdsApi: Got Meta ad', {
      ad_id: response.data.ad_id,
      creative_kind: response.data.creative_kind,
      has_editable_spec: response.data.creative_spec != null,
    });
    return response.data;
  },

  /**
   * Ad editor save — PATCH any combination of name / status / creative.
   * When `creative` is present, backend archive-and-recreates (Meta
   * doesn't allow in-place creative PATCH); response returns both old
   * and new creative IDs so cache can splice.
   *
   * Timeout matches the wizard's create-ad (image upload can be slow);
   * unlike `updateRSA`'s 90s, this needs 180s only when creating a new
   * creative — but a single timeout keeps the axios config simple.
   */
  async updateAd(
    adAccountId: string,
    adId: string,
    request: UpdateMetaAdRequest,
  ): Promise<UpdateMetaAdResponse> {
    debug.log('metaAdsApi: Patching Meta ad', {
      ad_account_id: adAccountId,
      ad_id: adId,
      has_name: request.ad_name !== undefined,
      has_status: request.status !== undefined,
      has_creative: request.creative !== undefined,
    });
    const response = await apiClient.patch<UpdateMetaAdResponse>(
      `/meta/ad-accounts/${encodeURIComponent(adAccountId)}/ads/${encodeURIComponent(adId)}`,
      request,
      // Long timeout only when a creative recreate is in flight (image
      // upload + create_ad_creative + update_ad + best-effort archive).
      // Name/status-only saves finish in <2s but a single timeout keeps
      // the client config uniform.
      { timeout: 180 * 1000 },
    );
    debug.log('metaAdsApi: Patched Meta ad', {
      ad_id: response.data.ad_id,
      new_creative_id: response.data.new_creative_id,
    });
    return response.data;
  },
};
